import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows, fetchAllRowsResult } from '../../lib/fetchAll';
import { submitTropeName, tagNameError } from '../../lib/tropeUtils';
import InfoTip from '../components/InfoTip';
import { logAudit } from '../lib/auditLog';

// PR 4 · The admin curation queue -- genre-subgenre-build-spec.md Part 6 /
// "new doc for genre", PR 4 section. Five things, word for word from both
// docs: promote a write-in to canon, merge it into an existing subgenre
// (re-pointing every book), reclassify it under a different genre, reject it
// as a trope, and a per-write-in book+author count shown for canon rows as
// well as pending ones -- "the entire signal for deciding between promote
// and merge", and the stored precondition for a later "swap" feature that is
// explicitly NOT built here (see PENDING_TAB_COPY below).
//
// Modelled on TropeQueueSection.jsx's PendingQueue -- same Pill/Btn look, same
// per-row load-then-enrich shape, same "every Supabase call destructures
// {error}" discipline. Deliberately does NOT copy that file's sweep/scan tab
// or its Aliases/Log tabs: those exist there to solve an embedding-similarity
// problem tropes have and subgenres don't (no automated duplicate-detection
// sweep is in scope here), and to browse trope_aliases, a table subgenres has
// no equivalent of -- subgenres alias on the row itself via alias_of. History
// for subgenre decisions lives in admin_audit_log like every other admin
// action, via the shared Audit Log section, not a bespoke tab.

// ── Helpers ───────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Shared UI (local copy, same convention as every other admin section --
//    confirmed against BooksSection.jsx / EventsSection.jsx / TropeQueueSection.jsx,
//    none of which import a shared Pill/Btn either) ──────────
function Pill({ children, color = 'gray' }) {
  const styles = {
    gray:  { background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    blue:  { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
    green: { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
    amber: { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
    purple:{ background: '#fdf4ff', color: '#6b21a8', border: '1px solid #d8b4fe' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 'var(--font-size-xxs)',
      fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-xxl)',
      letterSpacing: 0.2, whiteSpace: 'nowrap', flexShrink: 0,
      ...styles[color],
    }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = 'default', disabled, small }) {
  const variants = {
    default: { background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
    approve: { background: 'var(--feedback-success-bg)', color: '#065f46', border: '1px solid #86efac' },
    reject:  { background: '#fff8f8', color: '#991b1b', border: '1px solid #fca5a5' },
    merge:   { background: '#faf5ff', color: '#6b21a8', border: '1px solid #d8b4fe' },
    primary: { background: 'var(--neutral-800)', color: 'var(--brand-white)', border: '1px solid var(--neutral-800)' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontSize: small ? 11 : 11.5, fontWeight: 600,
      padding: small ? '4px 10px' : '6px 13px',
      borderRadius: 'var(--radius-md)', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
      opacity: disabled ? 0.5 : 1, ...variants[variant],
    }}>
      {children}
    </button>
  );
}

// The book+author count pill. Rendered identically on the Pending and Canon
// tabs -- "for canon rows as well as pending ones" is not a suggestion, it is
// the one requirement the spec calls out as "not optional" twice.
function CountPill({ counts, failed }) {
  if (failed) {
    return <Pill color="amber">counts didn't load</Pill>;
  }
  const books = counts?.book_count || 0;
  const authors = counts?.author_count || 0;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      <Pill color={books > 0 ? 'blue' : 'gray'}>{books} book{books === 1 ? '' : 's'}</Pill>
      <Pill color={authors > 0 ? 'purple' : 'gray'}>{authors} author{authors === 1 ? '' : 's'}</Pill>
    </span>
  );
}

// ── A canon subgenre, for the merge-target and reclassify pickers ──────────
// Loaded once per mount by the parent section and passed down, rather than
// each row fetching its own copy -- there are ~183 canon subgenres total
// (Part 1 of the spec), small enough to hold in memory and filter client-side
// with no server round trip per keystroke.
function useCanonList(genresById) {
  const [canon, setCanon] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    const rows = await fetchAllRows(() => supabase
      .from('subgenres')
      .select('id, genre_id, name')
      .eq('status', 'canon')
      .is('alias_of', null));
    setCanon((rows || []).map(r => ({ ...r, genre_name: genresById.get(r.genre_id)?.name || '?' })));
    setLoading(false);
  }, [genresById]);
  useEffect(() => { load(); }, [load]);
  return { canon, loading, reload: load };
}

// ── A searchable dropdown of canon subgenres, for Merge into... ────────────
// Plain text filter over the in-memory list above, not a second matcher --
// findSubgenreMatch (src/lib/genreTaxonomy.js) exists to catch a NEW write-in
// that might already mean something canon; here the admin is choosing among
// KNOWN canon rows on purpose, so a substring filter on name (and genre name,
// since "Romantasy" living under Fantasy while merging a Romance write-in
// into it is exactly the cross-genre case the spec's own worked example
// describes) is the right amount of help, not a semantic guess.
function MergeTargetPicker({ canon, excludeId, onPick, onCancel }) {
  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return canon
      .filter(c => c.id !== excludeId)
      .filter(c => !needle || c.name.toLowerCase().includes(needle) || c.genre_name.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [canon, excludeId, q]);
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--brand-cream)', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)' }}>
      <input
        autoFocus
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search canon subgenres to merge into…"
        style={{ width: '100%', fontSize: 12.5, padding: '7px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
      />
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
        {matches.length === 0 && (
          <span style={{ fontSize: 11.5, color: 'var(--neutral-400)', padding: '4px 2px' }}>
            {q.trim() ? 'No canon subgenre matches that.' : `${canon.length} canon subgenres, type to filter.`}
          </span>
        )}
        {matches.map(c => (
          <button key={c.id} onClick={() => onPick(c)} style={{
            textAlign: 'left', fontSize: 12.5, padding: '7px 10px', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: 'var(--font-body)',
          }}>
            <span style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>{c.name}</span>
            <span style={{ color: 'var(--neutral-400)' }}>{c.genre_name}</span>
          </button>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <Btn small onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

// A genre <select>, for Reclassify. Genres are the fixed list of 20 -- no
// write-in, ever (SETTLED, do not reopen) -- so this is a closed dropdown,
// never a search box.
function ReclassifyPicker({ genres, currentGenreId, onPick, onCancel }) {
  const [genreId, setGenreId] = useState('');
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--brand-cream)', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={genreId} onChange={e => setGenreId(e.target.value)} style={{ fontSize: 12.5, padding: '6px 8px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', fontFamily: 'var(--font-body)' }}>
        <option value="">Move to which genre?</option>
        {genres.filter(g => g.id !== currentGenreId).map(g => (
          <option key={g.id} value={g.id}>{g.name}</option>
        ))}
      </select>
      <Btn small variant="primary" disabled={!genreId} onClick={() => onPick(Number(genreId))}>Move</Btn>
      <Btn small onClick={onCancel}>Cancel</Btn>
    </div>
  );
}

// A single-line prompt for the trope name, for Reject as trope. Pre-filled
// with the subgenre's own name -- usually right unedited, but editable,
// because the wording that fits a subgenre chip does not always fit a trope
// (e.g. "Grumpy/Sunshine" as a subgenre write-in vs. the trope catalogue's
// own established spelling for the same idea).
function RejectAsTropePrompt({ initialName, onConfirm, onCancel, busy }) {
  const [name, setName] = useState(initialName);
  const [warning, setWarning] = useState('');

  function handleChange(v) {
    setName(v);
    if (warning) setWarning('');
  }

  // Refuse rather than truncate, and leave their text alone so they can edit
  // it down. See tagNameError in tropeUtils for why this is not a maxLength.
  function handleConfirm() {
    const clean = name.trim();
    const problem = tagNameError(clean);
    if (problem) { setWarning(problem); return; }
    onConfirm(clean);
  }
  return (
    <div style={{ marginTop: 8, padding: 10, background: '#fff8f8', borderRadius: 'var(--radius-md)', border: '1px solid #fca5a5' }}>
      <p style={{ fontSize: 11.5, color: '#991b1b', margin: '0 0 8px', lineHeight: 1.5 }}>
        Moves every book carrying this subgenre onto a <b>trope</b> with the name
        below (found or created there), then deletes this subgenre row.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          autoFocus
          value={name}
          onChange={e => handleChange(e.target.value)}
          style={{ flex: 1, minWidth: 160, fontSize: 12.5, padding: '7px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', fontFamily: 'var(--font-body)' }}
        />
        <Btn small variant="reject" disabled={busy || !name.trim()} onClick={handleConfirm}>
          {busy ? 'Working…' : 'Confirm reject'}
        </Btn>
        <Btn small onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
      {warning && (
        <p style={{ fontSize: 11, color: '#991b1b', margin: '6px 2px 0' }}>{warning}</p>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// One row's action state machine. Shared by both tabs so Merge/Reclassify
// (available in both) render and behave identically -- only which actions
// are offered differs (Promote/Reject are pending-only).
// ══════════════════════════════════════════════════════════════
function SubgenreRow({ item, genresById, canon, canonLoading, isPending, working, onPromote, onMerge, onReclassify, onReject, genres }) {
  const [openAction, setOpenAction] = useState(null); // null | 'merge' | 'reclassify' | 'reject'
  const genreName = genresById.get(item.genre_id)?.name || '?';
  const busy = working === item.id;

  return (
    <div style={{ padding: '14px 4px', borderBottom: '1px solid var(--brand-sand)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--neutral-800)' }}>{item.name}</span>
        <Pill color="blue">{genreName}</Pill>
        <CountPill counts={item.counts} failed={item.countsFailed} />
        {item.created_at && (
          <span style={{ fontSize: 11, color: 'var(--neutral-400)', marginLeft: 'auto' }}>{timeAgo(item.created_at)}</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {isPending && (
          <Btn variant="approve" disabled={busy} onClick={onPromote}>
            {busy ? 'Working…' : 'Promote to canon'}
          </Btn>
        )}
        <Btn variant="merge" disabled={busy} onClick={() => setOpenAction(o => o === 'merge' ? null : 'merge')}>
          Merge into…
        </Btn>
        <Btn disabled={busy} onClick={() => setOpenAction(o => o === 'reclassify' ? null : 'reclassify')}>
          Reclassify…
        </Btn>
        {isPending && (
          <Btn variant="reject" disabled={busy} onClick={() => setOpenAction(o => o === 'reject' ? null : 'reject')}>
            Reject as trope…
          </Btn>
        )}
      </div>

      {openAction === 'merge' && (
        canonLoading
          ? <p style={{ fontSize: 11.5, color: 'var(--neutral-400)', marginTop: 8 }}>Loading canon subgenres…</p>
          : <MergeTargetPicker
              canon={canon}
              excludeId={item.id}
              onCancel={() => setOpenAction(null)}
              onPick={(target) => { setOpenAction(null); onMerge(item, target); }}
            />
      )}
      {openAction === 'reclassify' && (
        <ReclassifyPicker
          genres={genres}
          currentGenreId={item.genre_id}
          onCancel={() => setOpenAction(null)}
          onPick={(newGenreId) => { setOpenAction(null); onReclassify(item, newGenreId); }}
        />
      )}
      {openAction === 'reject' && (
        <RejectAsTropePrompt
          initialName={item.name}
          busy={busy}
          onCancel={() => setOpenAction(null)}
          onConfirm={(tropeName) => { onReject(item, tropeName); setOpenAction(null); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB 1 — PENDING QUEUE
// ══════════════════════════════════════════════════════════════
function PendingQueue({ genres, genresById, canonHook, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const load = useCallback(async () => {
    setLoading(true);
    // Every pending row is, by construction, alias_of IS NULL -- "Authenticated
    // can propose subgenres" (PR1's migration) is WITH CHECK (status =
    // 'pending' AND alias_of IS NULL), so there is no pending-and-alias state
    // to filter out here.
    const [rows, countsResult] = await Promise.all([
      fetchAllRows(() => supabase
        .from('subgenres')
        .select('id, genre_id, name, created_at')
        .eq('status', 'pending')),
      fetchAllRowsResult(() => supabase.from('subgenre_curation_counts').select('*'), { orderColumn: 'subgenre_id' }),
    ]);
    // A view has no id column -- fetchAllRows defaults to ordering by 'id',
    // which fails against subgenre_curation_counts every time. countsFailed
    // lets CountPill tell 'didn't load' apart from 'genuinely zero'.
    const countsFailed = !countsResult.ok;
    const countsById = new Map((countsResult.rows || []).map(c => [c.subgenre_id, c]));
    const enriched = (rows || []).map(r => ({ ...r, counts: countsById.get(r.id), countsFailed }));
    // Highest book count first -- "that number is the entire signal for
    // deciding between promote and merge" (new doc for genre, PR 4), so the
    // write-ins most worth an admin's next minute surface first rather than
    // being sorted by when they happened to arrive.
    enriched.sort((a, b) => (b.counts?.book_count || 0) - (a.counts?.book_count || 0)
      || new Date(a.created_at) - new Date(b.created_at));
    setItems(enriched);
    onCountChangeRef.current?.(enriched.length);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handlePromote(item) {
    setWorking(item.id);
    const { error } = await supabase.from('subgenres').update({ status: 'canon' }).eq('id', item.id);
    if (error) { alert('Promote failed: ' + error.message); setWorking(null); return; }
    await logAudit('create', 'subgenre', `Promoted "${item.name}" (${genresById.get(item.genre_id)?.name || '?'}) to canon`, item.id);
    await canonHook.reload();
    await load();
    setWorking(null);
  }

  async function handleMerge(item, target) {
    setWorking(item.id);
    try {
      const { data: result, error } = await supabase.rpc('merge_subgenres', {
        p_alias_id: item.id, p_keep_id: target.id,
        p_alias_name: item.name, p_keep_name: target.name,
      });
      if (error) { alert(`Merge failed, nothing was changed: ${error.message}`); return; }
      await logAudit('merge', 'subgenre',
        `Merged "${item.name}" (${genresById.get(item.genre_id)?.name || '?'}) → "${target.name}" (${target.genre_name}) `
        + `(${result?.moved ?? 0} moved, ${result?.deduped ?? 0} already tagged)`,
        item.id);
      await load();
    } catch (err) { alert('Merge failed: ' + err.message); }
    finally { setWorking(null); }
  }

  async function handleReclassify(item, newGenreId) {
    setWorking(item.id);
    const { data: result, error } = await supabase.rpc('reclassify_subgenre', {
      p_subgenre_id: item.id, p_new_genre_id: newGenreId,
    });
    if (error) {
      // Most likely subgenres_genre_id_normalized_name_key -- the target
      // genre already has a subgenre with this same normalized name. Surfaced
      // rather than swallowed, same rule as every write this session's
      // reviews have touched.
      alert('Reclassify failed, nothing was changed: ' + error.message);
      setWorking(null);
      return;
    }
    await logAudit('update', 'subgenre',
      `Reclassified "${item.name}" from ${genresById.get(item.genre_id)?.name || '?'} to ${genresById.get(newGenreId)?.name || '?'} `
      + `(${result?.genre_backfilled ?? 0} book(s) given the new genre)`,
      item.id);
    await load();
    setWorking(null);
  }

  async function handleReject(item, tropeName) {
    setWorking(item.id);
    try {
      const resolved = await submitTropeName(tropeName, 'trope');
      if (!resolved.ok) {
        alert(`Nothing was changed -- "${tropeName}" did not reach the trope queue. Try again.`);
        return;
      }
      const { data: result, error } = await supabase.rpc('reject_subgenre_as_trope', {
        p_subgenre_id: item.id, p_trope_id: resolved.trope_id,
      });
      if (error) { alert(`Reject failed, nothing was changed: ${error.message}`); return; }
      await logAudit('delete', 'subgenre',
        `Rejected "${item.name}" (${genresById.get(item.genre_id)?.name || '?'}) as the trope "${tropeName}" `
        + `(${result?.moved ?? 0} moved, ${result?.deduped ?? 0} already tagged)`,
        item.id);
      await load();
    } catch (err) { alert('Reject failed: ' + err.message); }
    finally { setWorking(null); }
  }

  if (loading) return <p style={{ fontSize: 13, color: 'var(--neutral-400)', padding: '20px 4px' }}>Loading…</p>;
  if (items.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--neutral-400)', padding: '30px 4px', textAlign: 'center' }}>
        No pending write-ins. The queue has nothing in it until PR 2's form (or PR 3's reader surfaces) creates one.
      </p>
    );
  }

  return (
    <div>
      {items.map(item => (
        <SubgenreRow
          key={item.id} item={item} genresById={genresById} genres={genres}
          canon={canonHook.canon} canonLoading={canonHook.loading}
          isPending working={working}
          onPromote={() => handlePromote(item)}
          onMerge={handleMerge}
          onReclassify={handleReclassify}
          onReject={handleReject}
        />
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB 2 — CANON BROWSER
//
// Exists because the spec requires the book+author count "for canon rows as
// well as pending ones" and states plainly why: a future swap feature
// compares one canon row's counts against one pending row's. This tab is
// where that canon-side number lives and is the only place a canon subgenre
// can be merged or reclassified after the fact (a mis-filed canon row, or two
// canon rows that turned out to mean the same thing, do not stop existing
// just because PR 4 shipped). Swapping itself -- demoting a canon row back to
// pending to promote a bigger write-in -- is explicitly out of scope
// (FUTURE FEATURE section, both docs) and not built here.
// ══════════════════════════════════════════════════════════════
function CanonBrowser({ genres, genresById, canonHook, onCountChange }) {
  const [rowsWithCounts, setRowsWithCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genreFilter, setGenreFilter] = useState('');
  const [working, setWorking] = useState(null);
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const load = useCallback(async () => {
    setLoading(true);
    const countsResult = await fetchAllRowsResult(() => supabase.from('subgenre_curation_counts').select('*'), { orderColumn: 'subgenre_id' });
    const countsFailed = !countsResult.ok;
    const countsById = new Map((countsResult.rows || []).map(c => [c.subgenre_id, c]));
    setRowsWithCounts(canonHook.canon.map(r => ({ ...r, counts: countsById.get(r.id), countsFailed })));
    setLoading(false);
  }, [canonHook.canon]);

  useEffect(() => { load(); }, [load]);
  // Canon rows carry no "needs attention" badge -- onCountChange for this
  // tab is a no-op call kept only so the parent's signature stays uniform;
  // the sidebar badge counts pending write-ins, not the whole catalogue.
  useEffect(() => { onCountChangeRef.current?.(0); }, [rowsWithCounts]);

  const visible = useMemo(() => {
    const list = genreFilter ? rowsWithCounts.filter(r => r.genre_id === Number(genreFilter)) : rowsWithCounts;
    return [...list].sort((a, b) => (b.counts?.book_count || 0) - (a.counts?.book_count || 0));
  }, [rowsWithCounts, genreFilter]);

  async function reloadAll() { await canonHook.reload(); }

  async function handleMerge(item, target) {
    setWorking(item.id);
    try {
      const { data: result, error } = await supabase.rpc('merge_subgenres', {
        p_alias_id: item.id, p_keep_id: target.id,
        p_alias_name: item.name, p_keep_name: target.name,
      });
      if (error) { alert(`Merge failed, nothing was changed: ${error.message}`); return; }
      await logAudit('merge', 'subgenre',
        `Merged "${item.name}" (${genresById.get(item.genre_id)?.name || '?'}) → "${target.name}" (${target.genre_name}) `
        + `(${result?.moved ?? 0} moved, ${result?.deduped ?? 0} already tagged)`,
        item.id);
      await reloadAll();
    } catch (err) { alert('Merge failed: ' + err.message); }
    finally { setWorking(null); }
  }

  async function handleReclassify(item, newGenreId) {
    setWorking(item.id);
    const { data: result, error } = await supabase.rpc('reclassify_subgenre', {
      p_subgenre_id: item.id, p_new_genre_id: newGenreId,
    });
    if (error) { alert('Reclassify failed, nothing was changed: ' + error.message); setWorking(null); return; }
    await logAudit('update', 'subgenre',
      `Reclassified "${item.name}" from ${genresById.get(item.genre_id)?.name || '?'} to ${genresById.get(newGenreId)?.name || '?'} `
      + `(${result?.genre_backfilled ?? 0} book(s) given the new genre)`,
      item.id);
    await reloadAll();
    setWorking(null);
  }

  if (loading || canonHook.loading) return <p style={{ fontSize: 13, color: 'var(--neutral-400)', padding: '20px 4px' }}>Loading…</p>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <select value={genreFilter} onChange={e => setGenreFilter(e.target.value)} style={{ fontSize: 12.5, padding: '6px 8px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', fontFamily: 'var(--font-body)' }}>
          <option value="">All genres ({rowsWithCounts.length})</option>
          {genres.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>
      {visible.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--neutral-400)', padding: '30px 4px', textAlign: 'center' }}>Nothing canon here yet.</p>
      )}
      {visible.map(item => (
        <SubgenreRow
          key={item.id} item={item} genresById={genresById} genres={genres}
          canon={canonHook.canon} canonLoading={canonHook.loading}
          isPending={false} working={working}
          onPromote={() => {}}
          onMerge={handleMerge}
          onReclassify={handleReclassify}
          onReject={() => {}}
        />
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════
export default function SubgenreQueueSection({ onBadgeChange, isMobile }) {
  const [tab, setTab] = useState('queue');
  const [genres, setGenres] = useState([]);
  const [genresLoading, setGenresLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    (async () => {
      setGenresLoading(true);
      const { data, error } = await supabase.from('genres').select('id, name, sort_order').order('sort_order', { ascending: true });
      if (error) console.error('SubgenreQueueSection: could not load genres', error.message);
      setGenres(data || []);
      setGenresLoading(false);
    })();
  }, []);

  const genresById = useMemo(() => new Map(genres.map(g => [g.id, g])), [genres]);
  const canonHook = useCanonList(genresById);

  const handlePendingCount = useCallback((n) => {
    setPendingCount(n);
    onBadgeChange?.('subgenres', n);
  }, [onBadgeChange]);

  const TABS = [
    { id: 'queue', label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
    { id: 'canon', label: 'Canon' },
  ];

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 'var(--space-none)' }}>
          Genre &amp; subgenre queue
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', lineHeight: 1.6 }}>
          Promote, merge, reclassify, or reject subgenre write-ins.
          {pendingCount > 0 && (
            <span style={{ marginLeft: 'var(--space-10)', background: 'var(--feedback-error)', color: 'var(--brand-white)', fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-10)' }}>
              {pendingCount} pending
            </span>
          )}
          <span style={{ marginLeft: 'var(--space-6)' }}>
            <InfoTip width={340} text={
              `PENDING, subgenre write-ins nobody has curated yet. Each is fully `
              + `working on the books that carry it; it just isn't offered in `
              + `pickers or filter chips until promoted.\n\n`
              + `Books and authors are shown for every row on both tabs, because `
              + `that count is the whole promote-vs-merge signal: a write-in with `
              + `many books and one author is one person's habit; the same count `
              + `across many authors is a word the catalogue is missing.\n\n`
              + `Reject as trope moves every one of its books onto a trope tag and `
              + `deletes the subgenre row, freeing the name for a fresh write-in `
              + `later rather than leaving a dead row behind for it to collide with.`
            } />
          </span>
        </p>
      </div>

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px' : '20px 22px' }}>
        <div style={{ display: 'flex', borderBottom: '2px solid var(--brand-sand)', marginBottom: 18, marginTop: -4, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600,
              padding: isMobile ? '8px 12px' : '8px 16px',
              color: tab === t.id ? 'var(--neutral-800)' : 'var(--neutral-400)',
              border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontFamily: 'var(--font-body)',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {genresLoading ? (
          <p style={{ fontSize: 13, color: 'var(--neutral-400)', padding: '20px 4px' }}>Loading…</p>
        ) : (
          <>
            {tab === 'queue' && <PendingQueue genres={genres} genresById={genresById} canonHook={canonHook} onCountChange={handlePendingCount} />}
            {tab === 'canon' && <CanonBrowser genres={genres} genresById={genresById} canonHook={canonHook} onCountChange={() => {}} />}
          </>
        )}
      </div>
    </div>
  );
}
